import { useMemo } from 'react';

interface ColumnVirtualizerParams {
  scrollLeft: number;
  containerWidth: number;
  columnWidth: number;
  totalColumnCount: number;
  bufferSize?: number;
}

interface ColumnVirtualizerResult {
  firstVisibleIndex: number;
  lastVisibleIndex: number;
  totalWidth: number;
}

export function useColumnVirtualizer({
  scrollLeft,
  containerWidth,
  columnWidth,
  totalColumnCount,
  bufferSize = 4,
}: ColumnVirtualizerParams): ColumnVirtualizerResult {
  return useMemo(
    () => getVisibleRange({ scrollLeft, containerWidth, columnWidth, totalColumnCount, bufferSize }),
    [scrollLeft, containerWidth, columnWidth, totalColumnCount, bufferSize],
  );
}

export function getVisibleRange({
  scrollLeft,
  containerWidth,
  columnWidth,
  totalColumnCount,
  bufferSize = 4,
}: ColumnVirtualizerParams): ColumnVirtualizerResult {
  const totalWidth = columnWidth * totalColumnCount;

  if (totalColumnCount === 0 || columnWidth === 0) {
    return { firstVisibleIndex: 0, lastVisibleIndex: -1, totalWidth };
  }

  const firstVisible = Math.floor(scrollLeft / columnWidth);
  const lastVisible = Math.floor((scrollLeft + containerWidth - 1) / columnWidth);

  const firstVisibleIndex = Math.max(0, firstVisible - bufferSize);
  const lastVisibleIndex = Math.min(totalColumnCount - 1, lastVisible + bufferSize);

  return { firstVisibleIndex, lastVisibleIndex, totalWidth };
}

/**
 * The scrollLeft that brings column `column` wholly into view, or undefined when it is in view already. The columns
 * start after the row labels, `labelColumnWidth` wide (none on a phone), which stick to the view's left edge and cover
 * its first `labelColumnWidth` pixels. Where the view right of the labels is narrower than a column, the column's start
 * is shown: its right edge would push the start under the labels.
 */
export function getScrollLeftToShowColumn({
  column,
  columnWidth,
  labelColumnWidth,
  scrollLeft,
  viewWidth,
}: {
  column: number;
  columnWidth: number;
  labelColumnWidth: number;
  scrollLeft: number;
  viewWidth: number;
}): number | undefined {
  const columnLeft = labelColumnWidth + column * columnWidth;
  const columnRight = columnLeft + columnWidth;
  // a column shows only right of the sticky labels
  const viewLeft = scrollLeft + labelColumnWidth;
  const viewRight = scrollLeft + viewWidth;
  const leftAligned = columnLeft - labelColumnWidth;
  const isColumnWiderThanView = viewWidth - labelColumnWidth < columnWidth;
  if (columnLeft < viewLeft || (isColumnWiderThanView && columnLeft > viewLeft)) {
    return leftAligned;
  }
  if (columnRight > viewRight && !isColumnWiderThanView) {
    return columnRight - viewWidth;
  }
  return undefined;
}

/**
 * The scrollLeft that centres column `column` in the view right of the row labels, `labelColumnWidth` wide (none on a
 * phone), which stick to the view's left edge; 0 where the column is too near the start to be centred.
 */
export function getScrollLeftToCenterColumn({
  column,
  columnWidth,
  labelColumnWidth,
  viewWidth,
}: {
  column: number;
  columnWidth: number;
  labelColumnWidth: number;
  viewWidth: number;
}): number {
  // the column starts at labelColumnWidth + column * columnWidth, and the view right of the labels at
  // scrollLeft + labelColumnWidth: their centres meet here
  return Math.max(0, column * columnWidth + columnWidth / 2 - (viewWidth - labelColumnWidth) / 2);
}
